<template>
  <div>
    <!-- Compliance Content -->
    <div class="mb-0">
      <!-- Content -->
      <div class="mb-2 mt-1 d-flex align-items-center">
        <h2 class="mb-0">Target Market</h2>
        <hint-popup
          :id="18"
          class="ml-50"
        />
      </div>
      <!-- Loading Spinner for Target Market -->
      <b-row v-if="isLoadingTargetMarket" class="mb-0">
        <b-col cols="12" class="text-center py-2">
          <b-card>
            <LoadingContainer />
          </b-card>
        </b-col>
      </b-row>

      <!-- Error Message for Target Market -->
      <b-row v-else-if="!isLoadingTargetMarket && targetMarketError" class="mb-0">
        <b-col cols="12">
          <b-alert show variant="warning">
            <div class="alert-body">
              <feather-icon icon="AlertTriangleIcon" class="mr-50" />
              <span>Unable to load target market information at this time.</span>
            </div>
          </b-alert>
        </b-col>
      </b-row>

      <!-- Target Market Cards -->
      <b-row
        v-else-if="targetMarketInfo && targetMarketInfo.TARGETMARKET"
        class="match-height mb-0"
      >
        <b-col
          v-for="section in targetMarketInfo.TARGETMARKET"
          :key="section.sectionid"
          cols="12"
          md="4"
        >
          <b-card>
            <template #header>
              <div class="d-flex align-items-center">
                <b-avatar
                  size="38"
                  :variant="getSectionIconColor(section.sectionname)"
                  rounded
                  class="mr-1"
                >
                  <feather-icon
                    size="20"
                    :icon="getSectionIcon(section.sectionname)"
                  />
                </b-avatar>
                <b-card-title class="mb-0">
                  {{ section.sectionname }}
                </b-card-title>
              </div>
            </template>
            <b-card-body class="pt-0">
              <ul class="mb-0" style="padding-left: 0px; list-style-type: none;">
                <li
                  v-for="item in section.data"
                  v-show="item.success"
                  :key="item.position"
                  style="position: relative; padding-left: 15px;"
                >
                  <span style="position: absolute; left: 0;">▸</span>
                  {{ item.text }}
                </li>
              </ul>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- No Data Available FIX THIS -->
      <b-row v-else-if="!isLoadingTargetMarket" class="mb-0">
        <b-col cols="12">
          <b-alert show variant="info">
            <div class="alert-body">
              <feather-icon icon="InfoIcon" class="mr-50" />
              <span>No target market information available for this product.</span>
            </div>
          </b-alert>
        </b-col>
      </b-row>

      <!-- Preference Analysis Component -->
      <div class="mb-2 mt-2 d-flex align-items-center">
        <h2 class="mb-0">Suitability</h2>
        <hint-popup
          :id="19"
          class="ml-50"
        />
      </div>
      <div v-if="isLoadingPreference" class="text-center py-2 mt-3">
        <b-card>
          <LoadingContainer />
        </b-card>
      </div>
      <b-alert v-else-if="preferenceError" show variant="warning" class="mt-3">
        <div class="alert-body">
          <feather-icon icon="AlertTriangleIcon" class="mr-50" />
          <span>Unable to load preference information at this time.</span>
        </div>
      </b-alert>
      <div v-else>
        <!-- Suitability Loading/Error -->
        <b-alert v-if="suitabilityError" show variant="warning" class="mt-3">
          <div class="alert-body">
            <feather-icon icon="AlertTriangleIcon" class="mr-50" />
            <span>Unable to load suitability information at this time.</span>
          </div>
        </b-alert>
        <preference-analysis
          v-else
          :preference-info="preferenceInfo"
          :suitability-info="suitabilityInfo"
          :is-loading-preference="isLoadingPreference"
          :is-loading-suitability="isLoadingSuitability"
          :selected-preference-id="selectedPreferenceId"
          mode="single"
          show-category-hints
          @preference-changed="onPreferenceChanged"
        />

      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BAvatar,
  BAlert,
} from 'bootstrap-vue'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import HintPopup from '@/views/components/HintPopup.vue'
import PreferenceAnalysis from './PreferenceAnalysis.vue'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BAvatar,
    BAlert,
    PreferenceAnalysis,
    LoadingContainer,
    HintPopup,
  },
  props: {
    reportNumber: {
      type: [Number],
      required: true,
    },
    preferenceInfo: {
      type: Array,
      default: () => [],
    },
    isLoadingPreference: {
      type: Boolean,
      default: false,
    },
    preferenceError: {
      type: Boolean,
      default: false,
    },
    targetMarketInfo: {
      type: Object,
      default: null,
    },
    isLoadingTargetMarket: {
      type: Boolean,
      default: false,
    },
    targetMarketError: {
      type: Boolean,
      default: false,
    },
    suitabilityInfo: {
      type: Object,
      default: null,
    },
    isLoadingSuitability: {
      type: Boolean,
      default: false,
    },
    suitabilityError: {
      type: Boolean,
      default: false,
    },
    selectedPreferenceId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {}
  },
  methods: {
    collectCharts() {
      return {}
    },
    onPreferenceChanged(preferenceId) {
      this.$emit('preference-changed', preferenceId)
    },
    getSectionIcon(sectionName) {
      const name = sectionName.toLowerCase()
      if (name.includes('investment process')) return 'TrendingUpIcon'
      if (name.includes('personal circumstances') || name.includes('circumstance')) return 'UserIcon'
      if (name.includes('objective')) return 'TargetIcon'
      if (name.includes('market view')) return 'EyeIcon'
      if (name.includes('counterparty risk')) return 'TruckIcon'
      if (name.includes('market risk')) return 'BarChart2Icon'
      if (name.includes('inflation risk')) return 'TrendingUpIcon'
      if (name.includes('withdrawal') || name.includes('liquidity')) return 'ActivityIcon'
      if (name.includes('tax risk')) return 'PercentIcon'
      if (name.includes('risk')) return 'AlertTriangleIcon'
      if (name.includes('knowledge') || name.includes('experience')) return 'BookIcon'
      return 'CheckCircleIcon'
    },
    getSectionIconColor(sectionName) {
      const name = sectionName.toLowerCase()
      if (name.includes('investment process')) return 'light-primary'
      if (name.includes('personal circumstances') || name.includes('circumstance')) return 'light-info'
      if (name.includes('objective')) return 'light-success'
      if (name.includes('market view')) return 'light-info'
      if (name.includes('counterparty risk')) return 'light-danger'
      if (name.includes('market risk')) return 'light-danger'
      if (name.includes('inflation risk')) return 'light-warning'
      if (name.includes('withdrawal') || name.includes('liquidity')) return 'light-warning'
      if (name.includes('tax risk')) return 'light-warning'
      if (name.includes('risk')) return 'light-danger'
      if (name.includes('knowledge') || name.includes('experience')) return 'light-info'
      return 'light-primary'
    },
  },
}
</script>

<style lang="scss" scoped>
.header-card {
  margin-bottom: calc(2rem + 1px) !important;
}
</style>
